import { useEffect, useRef, useState } from "react";

type Props = {
  /** Shown when nothing is selected, e.g. "All stages". */
  allLabel: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
};

/*
    A checkbox dropdown. Empty selection means "no filter" rather than "nothing", which is
    why the button reads "All stages" at rest and names the single choice when there is one.
*/
export function FilterMenu({ allLabel, options, selected, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? allLabel)
        : `${allLabel.replace(/^All /, "")}: ${selected.length}`;

  const toggle = (value: string) =>
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );

  return (
    <span className="cat-filter" ref={ref}>
      <button
        type="button"
        className={`cat-filter__btn${selected.length ? " cat-filter__btn--active" : ""}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <i className="bi bi-chevron-down" />
      </button>
      {open && (
        <div className="cat-filter__menu" role="menu">
          {options.map((option) => (
            <label className="cat-filter__item" key={option.value}>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
    </span>
  );
}

export default FilterMenu;
